import api from '../../../../api/apiService';

import {
  ENDPOINTS,
} from '../../../../api/endpoints';

import type {
  PublicReportResponse,

  AccessCreationRequest,
  AccessCreationResponse,
  AccessCreationRecord,

  AccessRevocationRequest,
  AccessRevocationResponse,
  AccessRevocationRecord,

  AccessModificationRequest,
  AccessModificationResponse,
  AccessModificationRecord,

  TrackingReportRequest,
  TrackingReportResponse,
} from '../types/publicReport.types';

import publicReportMock from '../mocks/publicReport.mock';


// ============================================================
// REQUEST
// ============================================================

export interface PublicReportExportRequest {

  fromDate: string;

  toDate: string;

  pageNumber: number;

  pageSize: number;

  employeeCode?: string | null;
}


// ============================================================
// CONTENT TYPES
// ============================================================

const ZIP_CONTENT_TYPE =
  'application/zip';

const ZIP_CONTENT_TYPE_ALT =
  'application/x-zip-compressed';

const CSV_CONTENT_TYPE =
  'text/csv';

const CSV_CONTENT_TYPE_ALT =
  'application/csv';

const EXCEL_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';


// ============================================================
// GET PUBLIC REPORTS
// ============================================================

export const getPublicReports =
  async (): Promise<PublicReportResponse> => {

    return Promise.resolve(
      publicReportMock,
    );
  };


// ============================================================
// VALIDATE ACTIVITY LOG REQUEST
// ============================================================

const validateActivityLogRequest = (
  request: PublicReportExportRequest,
): void => {

  // ==========================================================
  // FROM DATE
  // ==========================================================

  if (!request.fromDate) {

    throw new Error(
      'From date is required.',
    );
  }


  // ==========================================================
  // TO DATE
  // ==========================================================

  if (!request.toDate) {

    throw new Error(
      'To date is required.',
    );
  }


  // ==========================================================
  // DATE RANGE
  // ==========================================================

  if (
    request.fromDate >
    request.toDate
  ) {

    throw new Error(
      'From date cannot be greater than To date.',
    );
  }


  // ==========================================================
  // PAGE NUMBER
  // ==========================================================

  if (
    !Number.isInteger(
      request.pageNumber,
    ) ||
    request.pageNumber < 1
  ) {

    throw new Error(
      'Page number must be greater than or equal to 1.',
    );
  }


  // ==========================================================
  // PAGE SIZE
  // ==========================================================

  if (
    !Number.isInteger(
      request.pageSize,
    ) ||
    request.pageSize < 1
  ) {

    throw new Error(
      'Page size must be greater than 0.',
    );
  }


  // ==========================================================
  // MAX PAGE SIZE
  // ==========================================================

  if (
    request.pageSize > 10000
  ) {

    throw new Error(
      'Page size cannot be greater than 10000.',
    );
  }
};


// ============================================================
// GET FILE NAME FROM CONTENT-DISPOSITION
// ============================================================

const getFileNameFromHeaders = (
  contentDisposition: unknown,
  fallbackFileName: string,
): string => {

  if (
    typeof contentDisposition !==
    'string'
  ) {

    return fallbackFileName;
  }


  // ==========================================================
  // UTF-8 FILE NAME
  // ==========================================================

  const utf8Match =
    contentDisposition.match(
      /filename\*=UTF-8''([^;]+)/i,
    );


  if (
    utf8Match?.[1]
  ) {

    try {

      return decodeURIComponent(
        utf8Match[1],
      ).trim();

    } catch {

      return utf8Match[1].trim();
    }
  }


  // ==========================================================
  // NORMAL FILE NAME
  // ==========================================================

  const normalMatch =
    contentDisposition.match(
      /filename="?([^";]+)"?/i,
    );


  if (
    normalMatch?.[1]
  ) {

    return normalMatch[1].trim();
  }


  // ==========================================================
  // FALLBACK
  // ==========================================================

  return fallbackFileName;
};


// ============================================================
// DOWNLOAD BLOB
// ============================================================

const downloadBlob = (
  blob: Blob,
  fileName: string,
): void => {

  // ==========================================================
  // VALIDATE
  // ==========================================================

  if (
    !blob ||
    blob.size === 0
  ) {

    throw new Error(
      'The downloaded file is empty.',
    );
  }


  // ==========================================================
  // CREATE OBJECT URL
  // ==========================================================

  const downloadUrl =
    window.URL.createObjectURL(
      blob,
    );


  // ==========================================================
  // CREATE LINK
  // ==========================================================

  const link =
    document.createElement(
      'a',
    );


  try {

    // ========================================================
    // URL
    // ========================================================

    link.href =
      downloadUrl;


    // ========================================================
    // FILE NAME
    // ========================================================

    link.download =
      fileName;


    link.setAttribute(
      'download',
      fileName,
    );


    // ========================================================
    // HIDE
    // ========================================================

    link.style.display =
      'none';


    // ========================================================
    // ADD TO DOM
    // ========================================================

    document.body.appendChild(
      link,
    );


    // ========================================================
    // AUTOMATIC DOWNLOAD
    // ========================================================

    link.click();

  } finally {

    // ========================================================
    // REMOVE LINK
    // ========================================================

    link.remove();


    // ========================================================
    // RELEASE OBJECT URL
    // ========================================================

    window.setTimeout(
      () => {

        window.URL.revokeObjectURL(
          downloadUrl,
        );

      },
      2000,
    );
  }
};


// ============================================================
// GET CONTENT TYPE
// ============================================================

const getResponseContentType = (
  response: {
    headers?: Record<string, unknown>;
  },
): string => {

  const headerValue =
    response.headers?.[
      'content-type'
    ];


  if (
    typeof headerValue ===
    'string'
  ) {

    return headerValue
      .split(';')[0]
      .trim()
      .toLowerCase();
  }


  return '';
};


// ============================================================
// ACTIVITY LOG RAW DATA EXPORT
//
// SAME BACKEND API
//
// POST
// /v1/employee-activity-log-summary/exportrawcsv
//
// Backend can return:
//
// 1. text/csv
//    When only one CSV file is generated.
//
// 2. application/zip
//    When multiple CSV files are generated.
//
// UI automatically downloads whichever one backend returns.
// ============================================================

export const exportActivityLogRawData =
  async (
    request: PublicReportExportRequest,
  ): Promise<string> => {

    // ==========================================================
    // VALIDATE
    // ==========================================================

    validateActivityLogRequest(
      request,
    );


    // ==========================================================
    // EMPLOYEE CODE
    //
    // Empty value is converted to null.
    // ==========================================================

    const employeeCode =
      request.employeeCode?.trim() ||
      null;


    // ==========================================================
    // REQUEST PAYLOAD
    // ==========================================================

    const payload = {

      fromDate:
        request.fromDate,

      toDate:
        request.toDate,

      employeeCode,

      pageSize:
        request.pageSize,
    };


    // ==========================================================
    // LOG REQUEST
    // ==========================================================

    console.log(
      '====================================================',
    );

    console.log(
      'ACTIVITY LOG RAW EXPORT REQUEST',
    );

    console.log(
      '====================================================',
    );

    console.log(
      payload,
    );


    // ==========================================================
    // CALL EXISTING BACKEND ENDPOINT
    // ==========================================================

    const response =
      await api.post(
        ENDPOINTS.PUBLIC_ACTIVITY_LOG_EXPORT,
        payload,
        {
          responseType:
            'blob',
        },
      );


    // ==========================================================
    // RESPONSE DATA
    // ==========================================================

    if (
      !response.data
    ) {

      throw new Error(
        'Empty response received from server.',
      );
    }


    // ==========================================================
    // RESPONSE CONTENT TYPE
    // ==========================================================

    const contentType =
      getResponseContentType(
        response,
      );


    // ==========================================================
    // RESPONSE DATA TO BLOB
    // ==========================================================

    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob(
            [
              response.data,
            ],
            {
              type:
                contentType ||
                CSV_CONTENT_TYPE,
            },
          );


    // ==========================================================
    // VALIDATE BLOB
    // ==========================================================

    if (
      blob.size === 0
    ) {

      throw new Error(
        'The server returned an empty export file.',
      );
    }


    // ==========================================================
    // CONTENT DISPOSITION
    // ==========================================================

    const contentDisposition =
      response.headers?.[
        'content-disposition'
      ];


    // ==========================================================
    // DETERMINE FILE TYPE
    // ==========================================================

    const isCsv =
      contentType ===
        CSV_CONTENT_TYPE ||
      contentType ===
        CSV_CONTENT_TYPE_ALT;


    const isZip =
      contentType ===
        ZIP_CONTENT_TYPE ||
      contentType ===
        ZIP_CONTENT_TYPE_ALT;


    // ==========================================================
    // DEFAULT FILE NAME
    // ==========================================================

    let fallbackFileName: string;


    if (isCsv) {

      fallbackFileName =
        `EmployeeRawData_${request.fromDate}_${request.toDate}.csv`;

    } else if (isZip) {

      fallbackFileName =
        `EmployeeRawData_${request.fromDate}_${request.toDate}.zip`;

    } else {

      // ========================================================
      // UNKNOWN CONTENT TYPE
      //
      // Use CSV as the safest fallback because the endpoint
      // generates CSV files.
      // ========================================================

      fallbackFileName =
        `EmployeeRawData_${request.fromDate}_${request.toDate}.csv`;
    }


    // ==========================================================
    // GET ACTUAL BACKEND FILE NAME
    // ==========================================================

    const fileName =
      getFileNameFromHeaders(
        contentDisposition,
        fallbackFileName,
      );


    // ==========================================================
    // LOG RESPONSE INFORMATION
    // ==========================================================

    console.log(
      '====================================================',
    );

    console.log(
      'ACTIVITY LOG RAW EXPORT RESPONSE',
    );

    console.log(
      '====================================================',
    );

    console.log(
      'File name:',
      fileName,
    );

    console.log(
      'Size:',
      blob.size,
      'bytes',
    );

    console.log(
      'Content-Type:',
      contentType,
    );

    console.log(
      'Is CSV:',
      isCsv,
    );

    console.log(
      'Is ZIP:',
      isZip,
    );


    // ==========================================================
    // HANDLE CSV
    // ==========================================================

    if (isCsv) {

      console.log(
        'Single CSV returned by backend.',
      );

      console.log(
        'Starting CSV browser download...',
      );


      downloadBlob(
        new Blob(
          [
            blob,
          ],
          {
            type:
              CSV_CONTENT_TYPE,
          },
        ),
        fileName.endsWith(
          '.csv',
        )
          ? fileName
          : `${fileName}.csv`,
      );


      console.log(
        'CSV download started successfully.',
      );


      return fileName.endsWith(
        '.csv',
      )
        ? fileName
        : `${fileName}.csv`;
    }


    // ==========================================================
    // HANDLE ZIP
    // ==========================================================

    if (isZip) {

      console.log(
        'Multiple CSV files returned as ZIP.',
      );

      console.log(
        'Starting ZIP browser download...',
      );


      downloadBlob(
        new Blob(
          [
            blob,
          ],
          {
            type:
              ZIP_CONTENT_TYPE,
          },
        ),
        fileName.endsWith(
          '.zip',
        )
          ? fileName
          : `${fileName}.zip`,
      );


      console.log(
        'ZIP download started successfully.',
      );


      return fileName.endsWith(
        '.zip',
      )
        ? fileName
        : `${fileName}.zip`;
    }


    // ==========================================================
    // UNKNOWN RESPONSE TYPE
    //
    // Backend should normally return either text/csv or
    // application/zip.
    //
    // Still download using the actual Content-Disposition
    // filename if available.
    // ==========================================================

    console.warn(
      'Unexpected export Content-Type:',
      contentType,
    );


    downloadBlob(
      blob,
      fileName,
    );


    return fileName;
  };


// ============================================================
// KEEP EXISTING FUNCTION NAME
//
// ActivityLogReportPage already imports this function.
// ============================================================

export const exportAndDownloadActivityLogRawData =
  async (
    request: PublicReportExportRequest,
  ): Promise<string> => {

    return exportActivityLogRawData(
      request,
    );
  };


// ============================================================
// SUMMARY REPORT - EXCEL
// ============================================================

export const exportSummaryReportExcel =
  async (
    request: PublicReportExportRequest,
  ): Promise<void> => {

    // ==========================================================
    // VALIDATION
    // ==========================================================

    if (
      !request.fromDate ||
      !request.toDate
    ) {

      throw new Error(
        'From date and To date are required.',
      );
    }


    if (
      request.fromDate >
      request.toDate
    ) {

      throw new Error(
        'From date cannot be greater than To date.',
      );
    }


    // ==========================================================
    // REQUEST
    // ==========================================================

    const response =
      await api.post(
        ENDPOINTS.PUBLIC_SUMMARY_REPORT_EXPORT,
        {
          fromDate:
            request.fromDate,

          toDate:
            request.toDate,
        },
        {
          responseType:
            'blob',
        },
      );


    // ==========================================================
    // VALIDATE RESPONSE
    // ==========================================================

    if (
      !response.data
    ) {

      throw new Error(
        'Empty response received from server.',
      );
    }


    // ==========================================================
    // CONTENT DISPOSITION
    // ==========================================================

    const contentDisposition =
      response.headers?.[
        'content-disposition'
      ];


    // ==========================================================
    // FILE NAME
    // ==========================================================

    const fileName =
      getFileNameFromHeaders(
        contentDisposition,
        `EmployeeActivityLog_${request.fromDate}_${request.toDate}.xlsx`,
      );


    // ==========================================================
    // CONTENT TYPE
    // ==========================================================

    const contentType =
      typeof response.headers?.[
        'content-type'
      ] === 'string'
        ? response.headers[
            'content-type'
          ] as string
        : EXCEL_CONTENT_TYPE;


    // ==========================================================
    // CREATE BLOB
    // ==========================================================

    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob(
            [
              response.data,
            ],
            {
              type:
                contentType,
            },
          );


    // ==========================================================
    // VALIDATE BLOB
    // ==========================================================

    if (
      blob.size === 0
    ) {

      throw new Error(
        'The server returned an empty Excel file.',
      );
    }


    // ==========================================================
    // DOWNLOAD
    // ==========================================================

    downloadBlob(
      blob,
      fileName,
    );
  };


// ============================================================
// ACCESS CREATION REPORT
// ============================================================

export const getAccessCreationReport =
  async (
    request: AccessCreationRequest,
  ): Promise<AccessCreationResponse> => {

    const response =
      await api.post<AccessCreationResponse>(
        ENDPOINTS.ACCESS_CREATION_REPORT,
        {
          fromDate:
            request.fromDate,

          toDate:
            request.toDate,

          pageNumber:
            request.pageNumber,

          pageSize:
            request.pageSize,

          search:
            request.search?.trim() ||
            null,
        },
      );


    return response.data;
  };


// ============================================================
// GET ALL ACCESS CREATION RECORDS
// ============================================================

export const getAllAccessCreationRecords =
  async (
    fromDate: string | null,
    toDate: string | null,
    search: string | null = null,
  ): Promise<AccessCreationRecord[]> => {

    const pageSize =
      10000;

    let pageNumber =
      1;

    let totalPages =
      1;

    const allRecords:
      AccessCreationRecord[] =
      [];


    do {

      const response =
        await getAccessCreationReport(
          {
            fromDate,

            toDate,

            pageNumber,

            pageSize,

            search:
              search?.trim() ||
              null,
          },
        );


      if (
        Array.isArray(
          response.data,
        )
      ) {

        allRecords.push(
          ...response.data,
        );
      }


      totalPages =
        response.totalPages ??
        0;


      pageNumber +=
        1;

    } while (
      pageNumber <=
      totalPages
    );


    return allRecords;
  };


// ============================================================
// ACCESS REVOCATION REPORT
// ============================================================

export const getAccessRevocationReport =
  async (
    request: AccessRevocationRequest,
  ): Promise<AccessRevocationResponse> => {

    const response =
      await api.post<AccessRevocationResponse>(
        ENDPOINTS.ACCESS_REVOCATION_REPORT,
        {
          fromDate:
            request.fromDate,

          toDate:
            request.toDate,

          pageNumber:
            request.pageNumber,

          pageSize:
            request.pageSize,

          search:
            request.search?.trim() ||
            null,
        },
      );


    return response.data;
  };


// ============================================================
// GET ALL ACCESS REVOCATION RECORDS
// ============================================================

export const getAllAccessRevocationRecords =
  async (
    fromDate: string | null,
    toDate: string | null,
    search: string | null = null,
  ): Promise<AccessRevocationRecord[]> => {

    const pageSize =
      10000;

    let pageNumber =
      1;

    let totalPages =
      1;

    const allRecords:
      AccessRevocationRecord[] =
      [];


    do {

      const response =
        await getAccessRevocationReport(
          {
            fromDate,

            toDate,

            pageNumber,

            pageSize,

            search:
              search?.trim() ||
              null,
          },
        );


      if (
        Array.isArray(
          response.data,
        )
      ) {

        allRecords.push(
          ...response.data,
        );
      }


      totalPages =
        response.totalPages ??
        0;


      pageNumber +=
        1;

    } while (
      pageNumber <=
      totalPages
    );


    return allRecords;
  };


// ============================================================
// ACCESS MODIFICATION REPORT
// ============================================================

export const getAccessModificationReport =
  async (
    request: AccessModificationRequest,
  ): Promise<AccessModificationResponse> => {

    const response =
      await api.post<AccessModificationResponse>(
        ENDPOINTS.ACCESS_MODIFICATION_REPORT,
        {
          fromDate:
            request.fromDate,

          toDate:
            request.toDate,

          pageNumber:
            request.pageNumber,

          pageSize:
            request.pageSize,

          search:
            request.search?.trim() ||
            null,
        },
      );


    return response.data;
  };


// ============================================================
// GET ALL ACCESS MODIFICATION RECORDS
// ============================================================

export const getAllAccessModificationRecords =
  async (
    fromDate: string | null,
    toDate: string | null,
    search: string | null = null,
  ): Promise<AccessModificationRecord[]> => {

    const pageSize =
      10000;

    let pageNumber =
      1;

    let totalPages =
      1;

    const allRecords:
      AccessModificationRecord[] =
      [];


    do {

      const response =
        await getAccessModificationReport(
          {
            fromDate,

            toDate,

            pageNumber,

            pageSize,

            search:
              search?.trim() ||
              null,
          },
        );


      if (
        Array.isArray(
          response.data,
        )
      ) {

        allRecords.push(
          ...response.data,
        );
      }


      totalPages =
        response.totalPages ??
        0;


      pageNumber +=
        1;

    } while (
      pageNumber <=
      totalPages
    );


    return allRecords;
  };

// ============================================================
// TRACKING - INSTALLATION COMPLIANCE REPORT
// ============================================================


export const getTrackingReport = async (
  payload: TrackingReportRequest,
): Promise<TrackingReportResponse> => {
  const response = await api.get<TrackingReportResponse>(
    ENDPOINTS.TRACKING_INSTALLATION_COMPLIANCE_REPORT,
    {
      params: {
        PageNumber: payload.PageNumber,
        PageSize: payload.PageSize,
        SearchText: payload.SearchText?.trim() || null,
      },
    },
  );

  return response.data;
};


